import React, { useCallback, useEffect, useState } from 'react';
import type { Help, HelpSection, HelpDefItem, HelpLink } from './helpTypes';
import './HelpPanel.css';

type HelpPanelProps = {
  help?: Help;
  helps?: Help[];
  activeIndex?: number;
  onTabChange?: (i: number) => void;
};

/**
 * The cloned screens' help links use the SOURCE's routes (`/integration-framework/…`,
 * `/benefit-realization`, `/ai-analysis/results`). Rewritten here, once, to where those
 * screens live on the platform — route prefixes are on §1.2's allowed list, and doing it in
 * the panel keeps the screens identical to the source. Unknown paths pass through.
 */
const PP = '/program_planning';
const ROUTES: Record<string, string> = {
  '/integration-framework/application-master-data': `${PP}/grouping-scope/integration-app-master-data`,
  '/integration-framework/integration-groupings': `${PP}/grouping-scope/integration-groupings`,
  '/integration-framework/phase-effort-planning': `${PP}/grouping-scope/phase-effort-planning`,
  '/integration-framework/application-dependencies': `${PP}/grouping-scope/application-dependencies`,
  '/integration-framework/resource-skillsets': `${PP}/resource-planning/resource-skillsets`,
  '/integration-framework/resource-capacity': `${PP}/resource-planning/resource-capacity`,
  '/integration-framework/resource-allocation': `${PP}/resource-planning/resource-allocation`,
  '/integration-framework/role-utilization': `${PP}/resource-planning/role-utilization`,
  '/integration-framework/disposition-control': `${PP}/roadmap-planning/roadmap-approval`,
  '/integration-framework/roadmap-schedule': `${PP}/roadmap-planning/roadmap-schedule`,
  '/roadmap/implementation': `${PP}/roadmap-planning/high-level-implementation-roadmap`,
  '/dashboard': `${PP}/executive-dashboard`,
  // Two links lead back into App Rationalization, where the source had those screens.
  '/ai-analysis/results': '/apr/application-evaluation/disposition-intelligence/analysis-results',
  '/customers/applications': '/apr/portfolio/applications',
};

function toPlatformPath(href: string): string {
  return ROUTES[href] ?? href;
}

const DEFAULT_ICON = 'ℹ️';
const DEFAULT_TITLE = 'How this works';

const HelpPanel: React.FC<HelpPanelProps> = ({ help, helps, activeIndex, onTabChange }) => {
  const panes: Help[] = helps ?? [help ?? {}];
  const multi = panes.length > 1;

  const [open, setOpen] = useState(false);
  const [internalActive, setInternalActive] = useState(0);

  const controlled = activeIndex !== undefined;
  const rawActive = controlled ? (activeIndex as number) : internalActive;
  const active = rawActive < 0 || rawActive >= panes.length ? 0 : rawActive;

  const close = useCallback(() => setOpen(false), []);

  const selectTab = useCallback(
    (i: number) => {
      const idx = i < 0 || i >= panes.length ? 0 : i;
      if (!controlled) setInternalActive(idx);
      if (onTabChange) onTabChange(idx);
    },
    [controlled, onTabChange, panes.length],
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close]);

  const pane = panes[active] ?? {};

  return (
    <>
      <button
        type="button"
        className="hlp-launch"
        onClick={() => setOpen((o) => !o)}
        title="How this works"
      >
        <span className="hlp-q">?</span> How this works
      </button>

      <div className={`hlp-overlay${open ? ' on' : ''}`} onClick={close} />
      <aside className={`hlp-drawer${open ? ' on' : ''}`} role="dialog" aria-label="How this works">
        <div className="hlp-head">
          <div className="hlp-title">
            <span className="hlp-ic">{pane.icon || DEFAULT_ICON}</span>
            <span>{pane.title || DEFAULT_TITLE}</span>
          </div>
          <button className="hlp-x" onClick={close} aria-label="Close">
            &times;
          </button>
        </div>

        {multi && (
          <div className="hlp-tabs" role="tablist">
            {panes.map((h, i) => (
              <button
                key={i}
                type="button"
                className={`hlp-tab${i === active ? ' on' : ''}`}
                onClick={() => selectTab(i)}
              >
                {h.tabLabel || h.title}
              </button>
            ))}
          </div>
        )}

        <div className="hlp-body">
          {pane.purpose && (
            <p className="hlp-purpose" dangerouslySetInnerHTML={{ __html: pane.purpose }} />
          )}

          {pane.doHere && pane.doHere.length > 0 && (
            <>
              <div className="hlp-lbl">What you do here</div>
              <ul className="hlp-list">
                {pane.doHere.map((d, i) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: d }} />
                ))}
              </ul>
            </>
          )}

          {pane.fits && (
            <>
              <div className="hlp-lbl">Where it fits</div>
              <div className="hlp-fits" dangerouslySetInnerHTML={{ __html: pane.fits }} />
            </>
          )}

          {pane.firstTime && (
            <div className="hlp-first">
              <span className="hlp-first-ic">&#9654;</span>
              <div className="hlp-first-c">
                <div className="hlp-first-lbl">First time? Do this</div>
                <div
                  className="hlp-first-txt"
                  dangerouslySetInnerHTML={{ __html: pane.firstTime }}
                />
              </div>
            </div>
          )}

          {pane.sections &&
            pane.sections.length > 0 &&
            pane.sections.map((sec: HelpSection, si: number) => (
              <React.Fragment key={si}>
                {sec.label && <div className="hlp-lbl">{sec.label}</div>}
                {sec.type === 'defs' ? (
                  <>
                    <div className="hlp-defs">
                      {(sec.items || []).map((it: HelpDefItem, ii: number) => (
                        <div className="hlp-def" key={ii}>
                          <span
                            className="hlp-def-term"
                            style={
                              it.color
                                ? {
                                    // Wash and a full-strength rule, exactly as the chips
                                    // on the screens do it. The rule used to be `55` —
                                    // a third of the colour — which read as a soft
                                    // outline on a pill, not as the edge of a chip.
                                    background: `${it.color}24`,
                                    color: it.color,
                                    border: `1px solid ${it.color}`,
                                  }
                                : undefined
                            }
                          >
                            {it.term}
                          </span>
                          <span
                            className="hlp-def-txt"
                            dangerouslySetInnerHTML={{ __html: it.def }}
                          />
                        </div>
                      ))}
                    </div>
                    {sec.note && (
                      <p className="hlp-note" dangerouslySetInnerHTML={{ __html: sec.note }} />
                    )}
                  </>
                ) : (
                  <div className="hlp-sec-html" dangerouslySetInnerHTML={{ __html: sec.html }} />
                )}
              </React.Fragment>
            ))}

          {pane.links && pane.links.length > 0 && (
            <div className="hlp-links">
              {pane.links.map((l: HelpLink, i: number) => (
                <a key={i} href={toPlatformPath(l.href)} className="hlp-link">
                  {l.label}
                </a>
              ))}
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default HelpPanel;
